import JhiDataUtils from '@/shared/data/data-utils.service';
import { AddonCategory, IAddonCategory } from '@/shared/model/addon-category.model';
import { Addon } from '@/shared/model/addon.model';
import { IAvailability } from '@/shared/model/availability.model';
import { IDiscount } from '@/shared/model/discount.model';
import { IMenuCategory, MenuCategory } from '@/shared/model/menu-category.model';
import { IMenuVariant, MenuVariant } from '@/shared/model/menu-variant.model';
import { IMenu, Menu } from '@/shared/model/menu.model';
import { IMultiLanguage } from '@/shared/model/multi-language.model';
import { IRestaurant } from '@/shared/model/restaurant.model';
import MenuCategoryService from '@/shared/services/menu-category.service';
import ProductInfoAdditiveService from '@/shared/services/product-info-additive.service';
import ProductInfoAllergenService from '@/shared/services/product-info-allergen.service';
import { ActionTree } from 'vuex';
import { RootState } from '../types';
import { MenusManagementType } from './types';

const menuCategoryService = new MenuCategoryService();
const productInfoAllergenService = new ProductInfoAllergenService();
const productInfoAdditiveService = new ProductInfoAdditiveService();
const jhiDataUtils = new JhiDataUtils();

export const actions: ActionTree<MenusManagementType, RootState> = {
  setMenuCategoriesTab({ commit }, newMenuCategoriesTab: number): void {
    commit('setMenuCategoriesTab', newMenuCategoriesTab);
  },
  setMenusTab({ commit }, newMenusTab: number): void {
    commit('setMenusTab', newMenusTab);
  },
  setStepper({ commit }, newStepper: number): void {
    commit('setStepper', newStepper);
  },
  setMenuCategories({ commit }, newMenuCategories: IMenuCategory[]): void {
    commit('setMenuCategories', newMenuCategories);
  },
  async retrieveMenuCategories({ commit, rootGetters }) {
    const menuCategories = await menuCategoryService.getMenuCategories(rootGetters['restaurantStore/getId']);
    commit('setMenuCategories', menuCategories);
    return true;
  },
  async retrieveProductInfos({ commit }) {
    const allergens = await productInfoAllergenService.retrievePublic();
    const additives = await productInfoAdditiveService.retrievePublic();
    commit('setProoductInfos', { allergens, additives });
  },
  setEnabled({ commit }, newEnabled: boolean): void {
    commit('setEnabled', newEnabled);
  },
  setNonfood({ commit }, newNonfood: boolean): void {
    commit('setNonfood', newNonfood);
  },
  setTitle({ commit }, newTitle: IMultiLanguage): void {
    newTitle.language = 'de';
    commit('setTitle', newTitle);
  },
  setDescription({ commit }, newDescription: IMultiLanguage): void {
    newDescription.language = 'de';
    commit('setDescription', newDescription);
  },
  setDiscount({ commit }, newDiscount: IDiscount): void {
    commit('setDiscount', newDiscount);
  },
  setMenus({ commit }, newMenus: IMenu[]): void {
    commit('setMenus', newMenus);
  },
  setMenuVariants({ commit }, newMenuVariants: IMenuVariant[]): void {
    commit('setMenuVariants', newMenuVariants);
  },
  setMenuAddonCategories({ commit }, newMenuAddonCategories: IAddonCategory[]): void {
    commit('setMenuAddonCategories', newMenuAddonCategories);
  },
  setMenuCategoryAvailabilities({ commit }, newAvailabilities: IAvailability[]): void {
    commit('setMenuCategoryAvailabilities', newAvailabilities);
  },
  setMenuVariantAvailabilities({ commit }, newAvailabilities: IAvailability[]): void {
    commit('setMenuVariantAvailabilities', newAvailabilities);
  },
  setRestaurant({ commit }, newRestaurant: IRestaurant): void {
    commit('setRestaurant', newRestaurant);
  },
  addNewMenuCategory({ commit, getters }, newMenuCategoryTitle: IMultiLanguage): void {
    const newMenuCategory = new MenuCategory();
    newMenuCategory.title = newMenuCategoryTitle;
    newMenuCategory.ordinal = getters.getMenuCategories.length + 1;
    commit('addNewMenuCategory', newMenuCategory);
  },
  deleteMenuCategory({ commit }): void {
    commit('deleteMenuCategory');
  },
  setMenuCategoryImage({ commit }, categoryImageFile: File): void {
    commit('setMenuCategoryImage', categoryImageFile);
  },
  setMenuCategoryFile({ state, commit, getters }, newImageFile: File): void {
    const convert = jhiDataUtils.setFileData(newImageFile, getters.getMenuCategories[state.menuCategoriesTab], 'image');
    setTimeout(() => {
      commit('setImage', convert.image);
      commit('setImageContentType', convert.imageContentType);
    }, 100);
  },
  addNewMenu({ state, commit }, newMenuTitle: IMultiLanguage): void {
    newMenuTitle.language = 'de';
    const newMenu = new Menu();
    newMenu.title = newMenuTitle;
    newMenu.ordinal = state.menuCategories[state.menuCategoriesTab].menus.length + 1;
    newMenu.nonfood = state.menuCategories[state.menuCategoriesTab].nonfood;
    commit('addNewMenu', newMenu);
  },
  deleteMenu({ commit }): void {
    commit('deleteMenu');
  },
  setMenuImage({ commit }, menuImageFile: File): void {
    commit('setMenuImage', menuImageFile);
  },
  setMenuImage64({ commit }, menuImageFile): void {
    commit('setMenuImage64', menuImageFile);
  },
  addNewMenuVariant({ state, commit }, newMenuVariantTitle: IMultiLanguage): void {
    newMenuVariantTitle.language = 'de';
    const newMenuVariant = new MenuVariant();
    newMenuVariant.title = newMenuVariantTitle;
    newMenuVariant.ordinal =
      state.menuCategories[state.menuCategoriesTab].menus[state.menusTab[state.menuCategoriesTab]].menuVariants.length + 1;
    commit('addNewMenuVariant', newMenuVariant);
  },
  deleteMenuVariant({ commit }): void {
    commit('deleteMenuVariant');
  },
  addNewAddonCategory({ state, commit }, newAddonCategoryTitle: IMultiLanguage): void {
    newAddonCategoryTitle.language = 'de';
    const newAddonCategory = new AddonCategory();
    newAddonCategory.title = newAddonCategoryTitle;
    newAddonCategory.ordinal =
      state.menuCategories[state.menuCategoriesTab].menus[state.menusTab[state.menuCategoriesTab]].menuVariants[state.stepper]
        .addonCategories.length + 1;
    commit('addNewAddonCategory', { newAddonCategory });
  },
  deleteAddonCategory({ commit }, i: number): void {
    commit('deleteAddonCategory', i);
  },
  addNewAddon({ state, commit }, { newAddonTitle, tab }: { newAddonTitle: IMultiLanguage; tab: number }): void {
    newAddonTitle.language = 'de';
    const newAddon = new Addon();
    newAddon.title = newAddonTitle;
    newAddon.ordinal =
      state.menuCategories[state.menuCategoriesTab].menus[state.menusTab[state.menuCategoriesTab]].menuVariants[state.stepper]
        .addonCategories[tab].addons.length + 1;
    commit('addNewAddon', { tab, newAddon });
  },
  deleteAddon({ commit }, { i, j }: { i: number; j: number }): void {
    commit('deleteAddon', { i, j });
  },
};
